import React, { useState } from 'react';
import classNames from 'classnames';
import { useTranslate } from '../../context/api-context/translations/use-translate';
import { useApi } from '../../context/api-context/use-api';
import { RicosEditorLazy } from '../ricos';
import { RatingsInput } from 'wix-ui-tpa/cssVars';
import { StarIcon } from '../icons/star-icon';
import { useContentForm } from '../../hooks/use-content-form';
import { COMMENT_BOX, COMMENT_BOX_PLACEHOLDER, LIST_ITEM, REPLY, TOP_LEVEL, RATING_INPUT, COMMENT_BOX_ERROR, } from '../../../common/constants/data-hooks';
import ListItemContainer from '../list-item-container/list-item-container';
import { useConfiguration } from '../../context/configuration-context/use-configuration';
import { useNavigationPrompt } from '../../hooks/use-navigation-prompt';
import { useCancelFlow, useCancelFlowExecutor } from '../../hooks/use-cancel-flow';
import { getIsMobile } from '../../../common/store/base-params/base-params-selectors';
import { ControlButtons } from './control-buttons/control-buttons';
import { withEditorContext } from '@wix/ricos';
import { waitForAnimationEnd, getExternalToolbarId, getPlaceholderKey, formatLabel, } from './comment-box.utils';
import { PlaceholderText } from './placeholder-text/placeholder-text';
import { getCurrentMember } from '../../../common/store/members/members-selectors';
import { CommentBoxInnerFooter, CommentBoxOuterFooter } from './footers';
import { st, classes } from './comment-box.st.css';
import { ContactDetails } from './contact-details/contact-details';
import { ErrorSmall } from '@wix/wix-ui-icons-common/on-stage';
import { usePreloadEditorPlugins } from '../../hooks/use-preload-editor-plugins';
const CommentBox = ({ onSubmit, onCancel, formName, className, mountExpanded = false, initialContent, isEdit, isReply, isInList, id, dataHook, onTyping, withGuestFlow, editorEvents, commentId, isMarked = false, forwardRef, rating, ratingLabel, onRatingChange, initialAuthorName, initialEmail, }) => {
    const { resourceId, placeholderText: customPlaceholderText, placeholderTextReply: customPlaceholderTextReply, publishButtonText, publishButtonTextReply, guestUsernameRequired, guestEmailRequired, } = useConfiguration();
    const preloadPlugins = usePreloadEditorPlugins();
    const [isExpanded, setIsExpanded] = React.useState(() => mountExpanded);
    const { currentMember, biInitiateImageUpload, biInitiateFileUpload, biCommentFormExpanded, biClickPublishSelectBtn, biShowCommentReplySubmissionWarning, isMobile, commentsState, } = useApi((state, actions) => ({
        currentMember: getCurrentMember(state),
        biCommentFormExpanded: actions.biCommentFormExpanded,
        isMobile: getIsMobile(state),
        biInitiateImageUpload: actions.biInitiateImageUpload,
        biInitiateFileUpload: actions.biInitiateFileUpload,
        biClickPublishSelectBtn: actions.biClickPublishSelectBtn,
        biShowCommentReplySubmissionWarning: actions.biShowCommentReplySubmissionWarning,
        commentsState: state.comments[resourceId],
    }));
    const interactionFetchState = commentsState?.type === 'READY' && commentsState.interactionFetchState
        ? commentsState.interactionFetchState
        : 'not_fetched';
    const autoFocus = React.useRef(true);
    const editorRef = React.useRef();
    const containerRef = React.useRef(null);
    const t = useTranslate();
    const contentForm = useContentForm({ initialContent, initialAuthorName, initialEmail, onTyping });
    const [errors, setErrors] = useState({});
    const shouldShowContactDetailsInput = isExpanded && !isEdit && withGuestFlow;
    const sendShowCommentReplySubmissionWarningBi = ({ email, validationResults, }) => {
        const { contentFormIsValid, emailIsValid, authorNameIsValid, ratingIsValid } = validationResults;
        const errorsList = [
            { condition: !authorNameIsValid && guestUsernameRequired, errorKey: 'enter_name' },
            { condition: !email && guestEmailRequired, errorKey: 'enter_email' },
            { condition: email && !emailIsValid, errorKey: 'incorrect_email' },
            { condition: ratingIsValid && !contentFormIsValid, errorKey: 'write_comment' },
            { condition: !ratingIsValid && contentFormIsValid, errorKey: 'leave_rating' },
            {
                condition: !contentFormIsValid && !ratingIsValid,
                errorKey: 'write_comment_and_leave_rating',
            },
        ];
        const warningReason = errorsList
            .filter((errorItem) => errorItem.condition)
            .map((errorItem) => errorItem.errorKey)
            .join(' / ');
        biShowCommentReplySubmissionWarning({
            commentId,
            warningReason,
            type: isReply ? 'reply' : 'comment',
            authorRole: withGuestFlow ? 'ANONYMOUS' : 'MEMBER',
        });
    };
    const validateForm = ({ asGuest }) => {
        const { email } = contentForm;
        const validationResults = contentForm.validate({
            asGuest,
            rating,
            isReply,
            guestUsernameRequired,
            guestEmailRequired,
        });
        const { contentFormIsValid, emailIsValid, authorNameIsValid, ratingIsValid } = validationResults;
        const ratingError = !ratingIsValid ? t('comment-box.error.rating-required') : undefined;
        const contentError = !contentFormIsValid && !ratingIsValid
            ? t('comment-box.error.comment-and-rating-required')
            : !contentFormIsValid
                ? t('comment-box.error.comment-required')
                : ratingError;
        const emailError = asGuest
            ? !email
                ? guestEmailRequired
                    ? t('comment-box.error.email-required')
                    : undefined
                : !emailIsValid
                    ? t('comment-box.error.email-incorrect')
                    : undefined
            : undefined;
        const authorNameError = asGuest && guestUsernameRequired && !authorNameIsValid
            ? t('comment-box.error.username-required')
            : undefined;
        sendShowCommentReplySubmissionWarningBi({ email, validationResults });
        const newErrors = {
            ...errors,
            email: asGuest ? emailError : errors.email,
            authorName: asGuest && guestUsernameRequired ? authorNameError : errors.authorName,
            content: contentError,
            rating: ratingError,
        };
        setErrors(newErrors);
        return { isFormValid: Object.values(newErrors).filter(Boolean).length === 0 };
    };
    const handleSubmit = async ({ asGuest }) => {
        const content = (await editorEvents.publish());
        autoFocus.current = false; // we only want to focus form input on initial mount and not after publish
        const _rating = (() => {
            if (rating) {
                return rating.value
                    ? { type: 'ADD', value: rating.value }
                    : rating.hasPrevious
                        ? { type: 'REMOVE' }
                        : undefined;
            }
            return undefined;
        })();
        const { isFormValid } = validateForm({ asGuest });
        if (!isFormValid) {
            return undefined;
        }
        onSubmit({
            content,
            rating: _rating,
            email: contentForm.email,
            authorName: contentForm.authorName,
        }, () => {
            contentForm.reset();
            setIsExpanded(false);
            setErrors({});
        }, asGuest);
    };
    const resolveCancelFlow = useCancelFlow({
        onCancel: () => onCancel(contentForm.content, () => {
            contentForm.reset();
            setIsExpanded(false);
            autoFocus.current = true;
            setErrors({});
        }, contentForm.getIsEmpty()),
        isReply,
        id,
    });
    useNavigationPrompt({ resolveCancelFlow, formIsMounted: isExpanded });
    const handleCancel = useCancelFlowExecutor(resolveCancelFlow);
    React.useImperativeHandle(forwardRef, () => ({
        getElementRef: () => containerRef.current,
        expand: async (options) => {
            if (typeof options?.autoFocus !== 'undefined') {
                autoFocus.current = options.autoFocus;
            }
            setIsExpanded(true);
            biCommentFormExpanded({ resourceId });
            containerRef.current && (await waitForAnimationEnd(containerRef.current));
        },
        close: () => setIsExpanded(false),
        focus: () => {
            if (editorRef.current) {
                editorRef.current?.focus('end');
                return 'focus_initiated';
            }
            return 'not_ready';
        },
    }));
    const shouldShowRatingInput = (() => {
        if (!rating || isReply) {
            return false;
        }
        return isMobile ? isExpanded : true;
    })();
    const placeholderText = (isReply ? customPlaceholderTextReply : customPlaceholderText) ??
        t(getPlaceholderKey({
            isExpanded,
            isMobile,
            isReply,
            withRating: !!rating,
        }));
    const renderPublishControls = () => {
        return (React.createElement(ControlButtons, { isEdit: isEdit, onSubmit: () => {
                biClickPublishSelectBtn({ resourceId, isReply });
                handleSubmit({ asGuest: withGuestFlow ? withGuestFlow : false });
            }, onCancel: handleCancel, publishButtonText: isReply ? publishButtonTextReply : publishButtonText }));
    };
    const innerContent = (React.createElement(React.Fragment, null,
        React.createElement("div", { className: st(classes.contentContainer, {
                isExpanded,
                isError: !!errors.content,
            }), onMouseEnter: preloadPlugins },
            shouldShowRatingInput ? (React.createElement("div", { className: classes.ratingInputContainer },
                React.createElement(RatingsInput, { className: st(classes.ratingInput, {
                        isDisabled: !onRatingChange || interactionFetchState === 'not_fetched',
                        isError: !!errors.rating,
                    }), getReactionLabel: (rating) => t('ratings-display.rating-value-aria-label', { rating }), showReactionLabel: false, "data-hook": RATING_INPUT, value: rating?.value ?? undefined, label: ratingLabel && formatLabel(ratingLabel, rating?.isRequired), labelLayout: isMobile ? 'labelTop' : 'labelAside', size: isMobile ? 'large' : 'small', disabled: !onRatingChange || interactionFetchState === 'not_fetched', onSelect: (newRating) => {
                        const value = newRating === rating?.value ? undefined : newRating;
                        onRatingChange?.(value);
                        setIsExpanded(true);
                    } }),
                isMobile && React.createElement("div", { className: classes.gradientOverlay }))) : null,
            isExpanded ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: classes.editorWrapper },
                    React.createElement(RicosEditorLazy, { setRef: (ref) => (editorRef.current = ref), key: contentForm.editorKey, onChange: (content, contentTraits) => contentForm.onChange({ content, contentTraits }), onBusyChange: contentForm.onBusyChange, placeholder: placeholderText, initialContent: contentForm.content || undefined, externalToolbarId: getExternalToolbarId(resourceId, formName), onAddPluginContent: (pluginType) => {
                            pluginType === 'image'
                                ? biInitiateImageUpload({ isReply, resourceId })
                                : biInitiateFileUpload({ isReply, resourceId });
                        }, autoFocus: autoFocus.current, contentId: commentId, fallback: React.createElement(PlaceholderText, { text: placeholderText }) })),
                React.createElement(CommentBoxInnerFooter, { className: classes.innerFooter, ricosToolbarId: getExternalToolbarId(resourceId, formName), publishControls: isMobile ? renderPublishControls() : undefined }))) : (React.createElement("button", { "data-hook": COMMENT_BOX_PLACEHOLDER, className: classes.placeholder, onClick: () => {
                    biCommentFormExpanded({ resourceId });
                    setIsExpanded(true);
                } },
                rating && isMobile ? React.createElement(StarIcon, { className: classes.starIcon }) : null,
                ' ',
                React.createElement(PlaceholderText, { text: placeholderText })))),
        !!errors.content && (React.createElement("div", { className: classes.error, "data-hook": COMMENT_BOX_ERROR },
            React.createElement(ErrorSmall, null),
            errors.content)),
        isExpanded && (React.createElement(CommentBoxOuterFooter, { currentMember: currentMember, publishControls: isMobile ? undefined : renderPublishControls(), isMobile: isMobile }))));
    return isInList ? (React.createElement(ListItemContainer, { ref: containerRef, id: id, isPending: false, isReply: isReply, dataHook: classNames(LIST_ITEM, isReply ? REPLY : TOP_LEVEL, COMMENT_BOX, dataHook), isMarked: isMarked },
        React.createElement("div", { className: st(classes.root, { isReply, isEdit, isMobile, withRating: !!rating }, className) },
            shouldShowContactDetailsInput && (React.createElement(ContactDetails, { contentForm: contentForm, authorNameError: errors.authorName, emailError: errors.email, isMobile: isMobile, guestUsernameRequired: guestUsernameRequired, guestEmailRequired: guestEmailRequired })),
            innerContent))) : (React.createElement("div", { id: id, ref: containerRef, "data-hook": classNames(COMMENT_BOX, dataHook), className: st(classes.root, { isReply, isMobile, withRating: !!rating }, className) },
        shouldShowContactDetailsInput && (React.createElement(ContactDetails, { contentForm: contentForm, authorNameError: errors.authorName, emailError: errors.email, isMobile: isMobile, guestUsernameRequired: guestUsernameRequired, guestEmailRequired: guestEmailRequired })),
        innerContent));
};
export default withEditorContext(CommentBox);
//# sourceMappingURL=comment-box.js.map